import type { Option } from "../Select/Select";
import { flagCodeList } from "./flagCodes";

/**
 * Every country the panel can draw a flag for, named by the browser's own ICU
 * data — `RU — Russia`, in code order.
 *
 * The codes ICU refuses to name are the flag package's plates rather than
 * places (`XX`, `EU`) and are left out: a picker offering one would be
 * offering a country that does not exist.
 *
 * It is a module constant and a shared one because the two screens that ask
 * this question — the roster row editor, which SETS a country, and the roster
 * filter, which narrows by one — must offer the same list. A filter built from
 * the rows on screen can only ever offer the countries of the first page, so a
 * country used by rows 51 to 200 is unreachable: the two hundred rows are
 * walked with the search box and these two filters, and that is only true if
 * the filter can name every country in the table.
 *
 * Each caller prepends its own «all» / «not given» entry, because those two
 * are not the same word.
 */
export const COUNTRY_OPTIONS: Option[] = buildCountryOptions();

function buildCountryOptions(): Option[] {
  const names = new Intl.DisplayNames(["en"], { type: "region" });
  const out: Option[] = [];
  for (const code of flagCodeList()) {
    let name: string | undefined;
    try {
      name = names.of(code);
    } catch {
      continue;
    }
    if (!name || name === code) continue;
    out.push({ value: code, label: `${code} — ${name}` });
  }
  return out;
}
